import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { CheckCell } from "modules/ThreeDepartmentDashboard/components/ThreeDepartmentDashboardTable/components/CheckCell";
import "modules/ThreeDepartmentDashboard/components/ThreeDepartmentDashboardTable/ThreeDepartmentDashboardTable.scss";
import { setThreeDepartmentFilters, useThreeDepartmentFilters, useThreeDepartmentList } from "modules/ThreeDepartmentDashboard/hooks";
import { PATHS } from "router/paths";
import { getUserFullName } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { PAGE_SIZE } from "utils/common";

// TODO: find a solution to this any
const columnHelper = createColumnHelper<any>();

export const ThreeDepartmentDashboardTable = () => {
  const { t } = useTranslation();

  const filters = useThreeDepartmentFilters();
  const { items, nbItems, nbPages, isFetching } = useThreeDepartmentList();

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: filters.page - 1, pageSize: filters.page_size }) : updater;
    setThreeDepartmentFilters({ ...filters, page: newState.pageIndex + 1 });
  };

  const handlePageSizeChange = (value: PAGE_SIZE) => setThreeDepartmentFilters({ ...filters, page_size: value, page: 1 });

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "wo_nr",
        size: 80,
        header: () => <span>{`${t("v8_wo", "WO")} # ${nbItems}`}</span>,
        cell: ({ row: { original } }) => <Link to={`${PATHS.APPOINTMENTS}/${original.id}`}>{original.wo_nr}</Link>
      }),
      columnHelper.accessor(row => row, {
        id: "appointment_date",
        size: 80,
        header: () => <span>{t("v8_appointment_date", "Appointment Date")}</span>,
        cell: ({ row: { original } }) => <span>{formatDate(original.time_car_app, DATE_FORMATS_FNS.dateMonthYear)}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "reg_number",
        size: 80,
        header: () => <span>{`${t("v8_reg_number", "Reg #")}`}</span>,
        cell: ({ row: { original } }) => (
          <Link to={`${PATHS.CAR_DETAIL}/${original.car_id}`}>
            {original.is_lease_company ? <Icon name="building" /> : <Icon name="info circle" />}
            {original.reg_number}
          </Link>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "receptionist",
        size: 70,
        header: () => (
          <span>
            <Icon className="user" /> {t("v8_reception", "Reception")}
          </span>
        ),
        cell: ({ row: { original } }) => (
          <CheckCell
            setAt={original.three_department_receptionist ? original.three_department_receptionist_set_at : null}
            userName={getUserFullName(original.three_department_receptionist_user)}
          />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "parts",
        size: 70,
        header: () => (
          <span>
            <Icon className="gear" /> {t("v8_parts", "Parts")}
          </span>
        ),
        cell: ({ row: { original } }) => (
          <CheckCell
            setAt={original.three_department_parts ? original.three_department_parts_set_at : null}
            userName={getUserFullName(original.three_department_parts_user)}
          />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "workshop",
        size: 70,
        header: () => (
          <span>
            <Icon className="wrench" /> {t("v8_workshop", "Workshop")}
          </span>
        ),
        cell: ({ row: { original } }) => (
          <CheckCell
            setAt={original.three_department_workshop ? original.three_department_workshop_set_at : null}
            userName={getUserFullName(original.three_department_workshop_user)}
          />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "remark",
        size: 600,
        header: () => <span>{t("v8_remark", "Remark")}</span>,
        cell: ({ row: { original } }) => {
          if (!original.three_department_remark) return null;
          return <Popup hoverable content={original.three_department_remark} trigger={<p className="remark-text">{original.three_department_remark}</p>} />;
        }
      })
    ],
    [t, nbItems]
  );

  return (
    <div className="ThreeDepartmentDashboardTable">
      <ReactTable
        isFetching={isFetching}
        columns={columns}
        data={items}
        pageCount={nbPages}
        pageSize={filters.page_size}
        totalItems={nbItems}
        pagination={{ pageIndex: filters.page - 1, pageSize: filters.page_size }}
        onPaginationChange={handlePaginationChange}
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
